import React from "react";
import { AlertTriangle, ArrowRight, LockKeyhole } from "lucide-react";

const FIELD_LABELS: Record<string, string> = {
  companyName: "Company Name",
  licenseNumber: "Company License Number",
  licenseState: "License State",
  licenseExpiry: "License Expiry Date",
  "W9 Document": "W-9 Document",
  "Contractor Licence Document": "Contractor License Document",
  License: "Contractor License",
};

type Props = {
  missingFields: string[];
  mode?: "warning" | "blocking";
  title?: string;
  description?: string;
  onUpdateProfile?: () => void;
  actionLabel?: string;
};

export const ProfileCompletionGate: React.FC<Props> = ({
  missingFields,
  mode = "blocking",
  title = "Profile incomplete",
  description = "Complete the required company details before continuing this contract.",
  onUpdateProfile,
  actionLabel = "Update Company Profile",
}) => {
  if (!missingFields?.length) return null;

  const isBlocking = mode === "blocking";

  return (
    <section
      className={`profile-completion-gate profile-completion-gate--${mode}`}
      role={isBlocking ? "alert" : "status"}
      aria-labelledby="profile-completion-title"
    >
      <span className="profile-completion-gate__icon" aria-hidden="true">
        <AlertTriangle size={20} strokeWidth={2.4} />
      </span>

      <div className="profile-completion-gate__content">
        <div className="profile-completion-gate__header">
          <div>
            <h3 id="profile-completion-title">{title}</h3>
            <p>{description}</p>
          </div>
          {onUpdateProfile && (
            <button type="button" className="profile-completion-gate__action" onClick={onUpdateProfile}>
              {actionLabel}
              <ArrowRight size={16} aria-hidden="true" />
            </button>
          )}
        </div>

        <div className="profile-completion-gate__requirements">
          <span className="profile-completion-gate__label">
            {isBlocking ? "Required before continuing:" : "Missing profile items:"}
          </span>
          <ul aria-label="Missing company profile requirements">
            {missingFields.map((field) => (
              <li key={field}>{FIELD_LABELS[field] ?? field}</li>
            ))}
          </ul>
        </div>

        {isBlocking && (
          <p className="profile-completion-gate__note">
            <LockKeyhole size={14} aria-hidden="true" />
            Later contract steps are locked until these items are completed.
          </p>
        )}
      </div>
    </section>
  );
};
